import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_COLORS } from '@givebutter/primitives/color-picker';
import { useToast } from '@givebutter/primitives/toast';
import { isAcceptableThemeColor, maskWholeNumber, unmaskNumber } from '@givebutter/util';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateCampaign } from 'api/campaigns';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import * as SettingsCard from 'components/settings-card';
import * as Tooltip from 'components/tooltip';
import { Settings } from 'constants/settings';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface MainDetailsFormValues {
    title: string;
    subtitle: string;
    goal: string;
    themeColor: string;
    isThemeColorEnabled: boolean;
}

const CampaignMainDetails = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const methods = Form.useForm<MainDetailsFormValues>({
        defaultValues: {
            title: campaign.title ?? '',
            subtitle: campaign.subtitle ?? '',
            goal: maskWholeNumber(campaign.goal),
            themeColor: campaign.themeColor?.value ?? '',
            isThemeColorEnabled: campaign.themeColor?.active ?? false,
        },
    });
    const { watch, handleSubmit, path, control } = methods;
    const isThemeColorEnabled = watch('isThemeColorEnabled');

    const { mutate: submit } = useMutation({
        mutationFn: (values: MainDetailsFormValues) => {
            const data = {
                title: values.title,
                subtitle: values.subtitle,
                goal: values.goal.length > 0 ? unmaskNumber(values.goal) : null,
                settings: [
                    {
                        code: Settings.CampaignSpecificThemeColor.Code,
                        active: values.isThemeColorEnabled,
                        value: values.themeColor,
                    },
                ],
            };

            return updateCampaign({ campaignId: campaign.id, data });
        },
        onSuccess: () => {
            toast.success('Updated main details!');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaign.id),
            });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update main details'),
    });

    return (
        <Form.Provider {...methods}>
            <SettingsCard.Root>
                <SettingsCard.LeftSide>
                    <SettingsCard.Heading>Main details</SettingsCard.Heading>
                    <p className={'text-sm text-gray-700'}>
                        Choose a title and theme color for your campaign.
                    </p>
                </SettingsCard.LeftSide>
                <SettingsCard.RightSide>
                    <form onSubmit={handleSubmit(submit as SubmitHandler<MainDetailsFormValues>)}>
                        <div className={'flex flex-col gap-4'}>
                            <Form.Container>
                                <Form.Label className={'text-base'} name={path('title')}>
                                    Title
                                    <Form.LabelAsterisk />
                                </Form.Label>
                                <Form.TextInputRoot
                                    control={control}
                                    name={'title'}
                                    rules={{ required: 'Please enter a campaign title' }}
                                >
                                    <Form.TextInputForm />
                                </Form.TextInputRoot>
                                <Form.Error name={path('title')} />
                            </Form.Container>
                            {campaign.type !== 'collect' && (
                                <Form.Container>
                                    <Form.Label className={'text-base'} name={path('subtitle')}>
                                        Subtitle
                                    </Form.Label>
                                    <p className={'text-sm text-gray-700'}>
                                        Large, bold text that appears at the top of your page.
                                    </p>
                                    <Form.TextInputRoot control={control} name={'subtitle'}>
                                        <Form.TextInputForm />
                                    </Form.TextInputRoot>
                                </Form.Container>
                            )}
                            <Form.Container>
                                <div className={'flex items-center gap-1'}>
                                    <Form.Label className={'text-base'} name={path('goal')}>
                                        Goal
                                    </Form.Label>
                                    <Tooltip.Root>
                                        <Tooltip.Trigger>
                                            <span className="sr-only">More info</span>
                                            <FontAwesomeIcon size={'sm'} icon={faQuestionCircle} />
                                        </Tooltip.Trigger>
                                        <Tooltip.Portal>
                                            <Tooltip.Content inverse={true}>
                                                <p>
                                                    You keep the money you raise, regardless of
                                                    whether you hit your goal.
                                                </p>
                                                <Tooltip.Arrow inverse={true} />
                                            </Tooltip.Content>
                                        </Tooltip.Portal>
                                    </Tooltip.Root>
                                </div>
                                <p className={'text-sm text-gray-700'}>
                                    Add a goal and progress bar to your page.
                                </p>
                                <Form.TextInputRoot
                                    control={control}
                                    name={'goal'}
                                    inputRootProps={{
                                        mask: 'whole-number',
                                    }}
                                >
                                    <InputCurrencyIcon />
                                    <Form.TextInputForm />
                                </Form.TextInputRoot>
                            </Form.Container>
                            <Form.Container>
                                <div className={'flex justify-between gap-2'}>
                                    <Form.Label
                                        className={'text-base'}
                                        name={path('isThemeColorEnabled')}
                                    >
                                        Theme color
                                    </Form.Label>
                                    <Form.Switch
                                        control={control}
                                        name={path('isThemeColorEnabled')}
                                    />
                                </div>
                                <p className={'text-sm text-gray-700'}>
                                    Customize your page with a theme color.
                                </p>
                                {isThemeColorEnabled && (
                                    <>
                                        <Form.UseFormColorPicker
                                            control={control}
                                            name={'themeColor'}
                                            colors={DEFAULT_COLORS.filter(
                                                color => color.toUpperCase() !== '#FFFFFF',
                                            )}
                                            rules={{
                                                validate: value =>
                                                    isAcceptableThemeColor(value)
                                                        ? true
                                                        : 'Choose a darker color for better readability.',
                                            }}
                                        />
                                        <Form.Error name={path('themeColor')} />
                                    </>
                                )}
                            </Form.Container>
                            <div>
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </div>
                        </div>
                    </form>
                </SettingsCard.RightSide>
            </SettingsCard.Root>
        </Form.Provider>
    );
};

export { CampaignMainDetails };
